/**
 * Plain FAQ data for the /groups audience page.
 *
 * Kept JSX-free on purpose so it can be imported from BOTH the
 * runtime page config (which renders the FAQ UI) AND from
 * `src/lib/seo-config.ts` (which feeds the prerendered FAQPage
 * JSON-LD). The seo-config build path must never pull in JSX,
 * icons, or component-tree dependencies.
 *
 * Rich-formatted answers (with links, lists, etc.) live as
 * `aNode` on the page config — not here.
 */

export type GroupsFaq = { q: string; a: string };

export const groupsFaqs: GroupsFaq[] = [
  {
    q: "How quickly do we get specialist guidance?",
    a: "Same day in most cases. Curbside is more of a real-time conversation, while eConsults are a thoughtful response after the specialist reviews the data. No phone tag.",
  },
  {
    q: "Is this for emergencies or active patient management?",
    a: "No. Beam isn't for emergencies or active management. It's an asynchronous specialist recommendation that can help improve the care plan.",
  },
  {
    q: "Does this replace referrals?",
    a: "No. It helps your team figure out which cases need a referral — and which ones they can confidently manage with specialist guidance.",
  },
  {
    q: "Can we bill for curbside consults?",
    a: "The curbside itself isn't separately billable, but specialist input may support more complete documentation and higher-complexity E/M coding when medically appropriate. Billing depends on payer rules, documentation, and medical necessity.",
  },
  {
    q: "Are eConsults billable?",
    a: "Beam supports several billing pathways your organization may review. Specialist-side pathways may include 99446–99449 or 99451. Requesting-provider work may include 99452 when separately supported. Your billing team decides what applies.",
  },
  {
    q: "Do we need a subscription?",
    a: "See the pricing grid for both pay-as-you-go and subscription options. Subscriptions unlock discounted per-consult pricing tiers for teams with predictable volume. Curbside Only stays free pay-per-consult — no subscription required.",
  },
  {
    q: "What about AI Clinical Reasoning?",
    a: "Free on every account. Get instant AI-powered clinical reasoning for quick questions, with human specialist curbsides and eConsults available when you need deeper input.",
  },
  {
    q: "How is Beam's AI different from a general medical chatbot?",
    a: "Beam's AI is built for real patient cases, not med-ed content. It runs each question across multiple leading models and compares outputs to strengthen clinical reasoning. It's focused, ad-free, independent of pharma and publisher incentives, and connects to a human specialist when needed. Backed by recent research in Nature, npj Digital Medicine, NEJM AI, and AHRQ.",
  },
];
